import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Col, Statistic, Typography, ConfigProvider, Drawer } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchMerchants, deleteMerchant } from '../../redux/MerchantsUser';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import MerchantBox from './MerchantBox';
import MerchantsTable from './MerchantsTable';
import CustomInput from '../../commonComponents/input';
import { Merchant } from './types';
import notification from '../../commonComponents/Notification/Notification';
import ConfirmDialog from '../../commonComponents/ConfirmDialog';
import {
  Container,
  StyledCard,
  StatsRow,
  StatCard,
  HeaderSection,
  TopControlBar,
  AddButton,
  DrawerContentWrapper,
} from './styles';

const { Title } = Typography;

const Merchants: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState<Merchant | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [merchantToDelete, setMerchantToDelete] = useState<Merchant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { merchants, loading } = useSelector((state: RootState) => state.merchants);
  const hasFetchedRef = useRef(false);

  const filteredMerchants = useMemo(() => {
    if (!Array.isArray(merchants)) return [];
    if (!searchQuery) return merchants;
    const query = searchQuery.toLowerCase();
    return merchants.filter(
      (merchant) =>
        merchant?.name?.toLowerCase().includes(query) ||
        merchant?.username?.toLowerCase().includes(query) ||
        merchant?.email?.toLowerCase().includes(query)
    );
  }, [merchants, searchQuery]);

  const paginatedMerchants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredMerchants.slice(startIndex, endIndex);
  }, [filteredMerchants, currentPage, pageSize]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchMerchants());
    }
  }, [dispatch]);

  const handleAddClick = () => {
    setCurrentMerchant(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (merchant: Merchant) => {
    setCurrentMerchant(merchant);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setCurrentMerchant(null);
  };

  const handleDeleteClick = (merchant: Merchant) => {
    setMerchantToDelete(merchant);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!merchantToDelete) return;
    
    setIsDeleting(true);
    const result = await dispatch(deleteMerchant({ type: 'merchant', id: merchantToDelete.id }));
    setIsDeleting(false);
    
    if (deleteMerchant.fulfilled.match(result)) {
      notification.success('Merchant deleted successfully');
      setIsDeleteModalOpen(false);
      setMerchantToDelete(null);
      dispatch(fetchMerchants());
    } else {
      const errorMsg = typeof result.payload === 'string' ? result.payload : 'Error deleting merchant';
      notification.error('Delete Failed', errorMsg);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setMerchantToDelete(null);
    }
  };

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <StatsRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <StatCard>
                <Statistic
                  title="Total Merchants"
                  value={merchants.length}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <StatCard>
                <Statistic
                  title="Filtered Results"
                  value={filteredMerchants.length}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <StatCard
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/admin/merchant-earnings')}
              >
                <Statistic
                  title="Merchant Earnings"
                  value="View Overview"
                  valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
                  prefix={<UserOutlined style={{ color: '#fa8c16' }} />}
                />
              </StatCard>
            </Col>
          </StatsRow>

          <StyledCard>
            <HeaderSection>
              <div>
                <Title level={2} style={{ margin: 0 }}>
                  Merchants
                </Title>
                <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                  Manage your merchant accounts and partnerships
                </Typography.Text>
              </div>
              <AddButton
                type="primary"
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleAddClick}
                size="large"
              >
                Add Merchant
              </AddButton>
            </HeaderSection>

            <TopControlBar>
              <CustomInput
                placeholder="Search merchants by name, username, or email"
                value={searchQuery}
                onChange={(value: string) => setSearchQuery(value)}
                allowClear
                width="300px"
              />
            </TopControlBar>

            <MerchantsTable
              merchants={paginatedMerchants}
              currentPage={currentPage}
              pageSize={pageSize}
              total={filteredMerchants.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              loading={loading.merchants}
            />
          </StyledCard>

          <Drawer
            title={currentMerchant ? 'Edit Merchant' : 'Add Merchant'}
            placement="right"
            onClose={handleDrawerClose}
            open={isDrawerOpen}
            width={600}
            destroyOnClose
            styles={{
              body: {
                padding: 0,
                background: isDarkMode ? '#1a202c' : '#ffffff',
              },
              header: {
                background: isDarkMode ? '#1a202c' : '#ffffff',
                borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
              },
            }}
          >
            <DrawerContentWrapper>
              <MerchantBox
                initialMerchant={currentMerchant}
                onSave={() => {
                  setIsDrawerOpen(false);
                  handleDrawerClose();
                  dispatch(fetchMerchants());
                }}
                onCancel={handleDrawerClose}
                fetchMerchants={() => dispatch(fetchMerchants())}
              />
            </DrawerContentWrapper>
          </Drawer>

          <ConfirmDialog
            isOpen={isDeleteModalOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Merchant"
            message={
              merchantToDelete
                ? `Are you sure you want to delete "${merchantToDelete.name || merchantToDelete.email || merchantToDelete.username}"? This action cannot be undone.`
                : "Are you sure you want to delete this merchant? This action cannot be undone."
            }
            confirmText="Delete"
            cancelText="Cancel"
            confirmColorScheme="red"
            loading={isDeleting}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default Merchants;